import { client } from "../../../../api/client";

const stringifyIfNeeded = (value) => {
  if (value === null || value === undefined) return null;
  return typeof value === "string" ? value : JSON.stringify(value);
};

const parseObjectField = (value) => {
  if (value === null || value === undefined || value === "") return {};

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  return {};
};

const parseArrayField = (value) => {
  if (value === null || value === undefined || value === "") return [];

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [];
};

const normalizeTemplateFromApi = (item) => {
  if (!item || typeof item !== "object") return item;

  return {
    ...item,
    customFields: parseArrayField(item.customFields),
    customSections: parseArrayField(item.customSections),
    customFieldsLayout: parseArrayField(item.customFieldsLayout),
    previewSectionValues: parseObjectField(item.previewSectionValues),
  };
};

const normalizeTemplateResponse = (data) => {
  if (Array.isArray(data)) {
    return data.map((item) => normalizeTemplateFromApi(item));
  }

  if (data && typeof data === "object" && Array.isArray(data.quoteTemplateItems)) {
    return {
      ...data,
      quoteTemplateItems: data.quoteTemplateItems.map((item) => normalizeTemplateFromApi(item)),
    };
  }

  return normalizeTemplateFromApi(data);
};

const normalizePreviewTemplateForApi = (template) => {
  if (!template || typeof template !== "object") return template;

  return {
    ...template,
    customFields: stringifyIfNeeded(template.customFields),
    customSections: stringifyIfNeeded(template.customSections),
    customFieldsLayout: stringifyIfNeeded(template.customFieldsLayout),
    previewSectionValues: stringifyIfNeeded(template.previewSectionValues),
  };
};

const normalizeDocumentPreviewRequest = (request) => {
  if (!request || typeof request !== "object") return request;

  return {
    ...request,
    template: normalizePreviewTemplateForApi(request.template),
  };
};

export const fetchQuoteTemplate = async () => {
  if (process.env.REACT_APP_USELOCALDATA === "true") {
    return require("./_local/_quoteTemplateData.json");
  } else {
    try {
      const response = await client.get(process.env.REACT_APP_GATEWAY_URI, "templatesapi/quotetemplate");
      return normalizeTemplateResponse(response.data);
    } catch (error) {
      console.error("Error fetching quote template:", error);
      return [];
    }
  }
}

export const fetchQuoteTemplateByFilter = async (body) => {
    if (process.env.REACT_APP_USELOCALDATA === "true") { 
        return require("./_local/_quoteTemplateData.json");
    } else {
        try {
          const response = await client.post(process.env.REACT_APP_GATEWAY_URI,"templatesapi/quotetemplate/filter", body);
          return normalizeTemplateResponse(response.data);
        } catch (error) {
            console.error("Error fetching quote template:", error);
            return [];
        }
      }
}

export const deleteQuoteTemplate = async (id) => {
    if (process.env.REACT_APP_USELOCALDATA === "false") {
        try {
            const response = await client.delete(process.env.REACT_APP_GATEWAY_URI, `templatesapi/quotetemplate/${id}`);
            return response.data;
        } catch (error) {
            console.error("Error deleting quote template:", error);
            return [];
        }
    } else {
        return { "data": { newId: Math.floor(Math.random() * 100000) } };
    }
};

export const createQuoteTemplate = async (body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
        const response = await client.post(process.env.REACT_APP_GATEWAY_URI,"templatesapi/quotetemplate", body);
        return response.data;
    } catch (error) {
        console.error("Error creating quote template:", error);
        return [];
    }
  } else {
    return { "data": { newId: Math.floor(Math.random() * 100000) } };
  }
};


const normalizeUpdateBody = (body) => {
  if (!body || typeof body !== "object") return body;

  return {
    ...body,
    customFields: stringifyIfNeeded(body.customFields),
    customSections: stringifyIfNeeded(body.customSections),
    customFieldsLayout: stringifyIfNeeded(body.customFieldsLayout),
    previewSectionValues: stringifyIfNeeded(body.previewSectionValues),
  };
};

export const updateQuoteTemplate = async (id, body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
        const response = await client.put(process.env.REACT_APP_GATEWAY_URI,`templatesapi/quotetemplate/${id}`, normalizeUpdateBody(body));
        return response.data;
    } catch (error) {
        console.error("Error updating quote template:", error);
        return [];
    }
  }
};

export const replaceQuoteTemplateFile = async (id, body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.put(process.env.REACT_APP_GATEWAY_URI, `templatesapi/quotetemplate/replacefile/${id}`, body);
      return response.data;
    } catch (error) {
      console.error("Error replacing quote template file:", error);
      return null;
    }
  } else {
    // mimic the backend echoing back the key it actually stored the file under
    const file = body?.get?.("file");
    return { s3Key: file ? `quotetemplates/${file.name}` : null };
  }
};

export const getAWSSettings = async () => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.get(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/quotetemplate/getawssettings');
      return response.data;
    } catch (error) {
        console.error("Error getting AWS Settings:", error);
        return [];
    }
  }
};

export const getPreSignedUrl = async (key) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.post(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/quotetemplate/presignedurl/key', key);
      return response.data;
    } catch (error) {
        console.error("Error getting presigned url:", error);
        return [];
    }
  }
};

export const generateDocumentPreview = async (body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const normalizedBody = normalizeDocumentPreviewRequest(body);
      const normalizedQuotePreviewRequest = normalizeDocumentPreviewRequest(body?.quotePreviewRequest ?? body);

      const payload = {
        ...normalizedBody,
        quotePreviewRequest: normalizedQuotePreviewRequest,
      };

      const response = await client.post(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/documenttemplate/generatedocumentpreview', payload);
      return response.data;
    } catch (error) {
        console.error("Error generating document preview:", error);
        return [];
    }
  }
};

export const generateSheetPreview = async (body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.post(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/exporttemplate/generatesheetpreview', body);
      return response.data;
    } catch (error) {
        console.error("Error generating sheet preview:", error);
        return [];
    }
  }
};

export const generateDocument= async (body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const payload = normalizeDocumentPreviewRequest(body);
      const response = await client.post(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/documenttemplate/generatedocument', payload);
      return response.data;
    } catch (error) {
        console.error("Error generating document:", error);
        return null;
    }
  }
};

export const generateSheet = async (body) => {
  if (process.env.REACT_APP_USELOCALDATA === "false") {
    try {
      const response = await client.post(process.env.REACT_APP_GATEWAY_URI, 'templatesapi/exporttemplate/exporttemplate', body);
      return response.data;
    } catch (error) {
        console.error("Error generating sheet:", error);
        return null;
    }
  }
};